import { useTranslation } from 'react-i18next';

import Constants from 'constants/index';

import { TranslationKey } from '@/i18n';

export type Label = Record<
  string,
  { translationString: string; value: boolean }
>;

const planCategoriesMap = {
  complianceLabels: {
    [Constants.PLAN_CATEGORIES.COMPLIANCE]:
      'TrainingPlans.planCategories.compliance',
  },
  statusLabels: {
    [Constants.PLAN_STATUS.INCLUDE_COMPLETED]:
      'TrainingPlans.filtering.includeCompletedNoColon',
  },
  noComplianceLabels: {
    [Constants.PLAN_CATEGORIES.BACK_OF_HOUSE]:
      'TrainingPlans.planCategories.backOfHouse',
    [Constants.PLAN_CATEGORIES.FRONT_OF_HOUSE]:
      'TrainingPlans.planCategories.frontOfHouse',
    [Constants.PLAN_CATEGORIES.GENERAL]: 'TrainingPlans.planCategories.general',
    [Constants.PLAN_CATEGORIES.HOSPITALITY]:
      'TrainingPlans.planCategories.hospitality',
    [Constants.PLAN_CATEGORIES.LEADERSHIP]:
      'TrainingPlans.planCategories.leadership',
    [Constants.PLAN_CATEGORIES.ONBOARDING]:
      'TrainingPlans.planCategories.onboarding',
  },
};

type PlanCategory = keyof typeof planCategoriesMap;

const getLabels = (mapLabels: (labels: Record<string, string>) => Label) =>
  Object.entries(planCategoriesMap).reduce(
    (acc, [key, val]) => {
      acc[key as PlanCategory] = mapLabels(val);
      return acc;
    },
    {} as Record<PlanCategory, Label>,
  );

export const useCategoryLabelsGrouped = (
  currentFilters: string[],
  noCompliance?: boolean,
  includeStatus?: boolean,
): Label | Label[] => {
  const { t } = useTranslation();

  const mapLabels = (labels: Record<string, string>) =>
    Object.entries(labels).reduce<Label>(
      (acc, [key, translationKey]) => ({
        ...acc,
        [key]: {
          translationString: t(translationKey as TranslationKey),
          value: currentFilters.includes(key),
        },
      }),
      {},
    );

  const { complianceLabels, noComplianceLabels, statusLabels } =
    getLabels(mapLabels);

  if (noCompliance) {
    return noComplianceLabels;
  } else if (!includeStatus) {
    return [complianceLabels, noComplianceLabels];
  } else return [complianceLabels, noComplianceLabels, statusLabels];
};

export const useCategoryLabels = (
  currentFilters: string[],
  noCompliance?: boolean,
  includeStatus = false,
) => {
  const groups = useCategoryLabelsGrouped(
    currentFilters,
    noCompliance,
    includeStatus,
  );
  return Array.isArray(groups)
    ? groups.reduce((arr, group) => ({ ...arr, ...group }))
    : groups;
};

export const useCategoryLabels_v2 = (currentFilters: string[]) => {
  const { t } = useTranslation();

  const mapLabels = (labels: Record<string, string>) =>
    Object.entries(labels).reduce<
      Record<string, { translationString: string; value: boolean }>
    >(
      (acc, [key, translationKey]) => ({
        ...acc,
        [key]: {
          translationString: t(translationKey as TranslationKey),
          value: currentFilters.includes(key),
        },
      }),
      {},
    );

  const complianceLabels = mapLabels(planCategoriesMap.complianceLabels);
  const noComplianceLabels = mapLabels(planCategoriesMap.noComplianceLabels);
  const statusLabels = mapLabels(planCategoriesMap.statusLabels);

  const allLabels = Object.fromEntries(
    Object.values({
      complianceLabels,
      noComplianceLabels,
      statusLabels,
    }).flatMap(v => Object.entries(v)),
  );

  return {
    complianceLabels,
    noComplianceLabels,
    statusLabels,
    allLabels,
  };
};
